import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';

import { AdminHeader } from './components/admin-header';
import { AdminSidebar } from './components/admin-sidebar';
import { CommandPalette, useCommandPalette } from './components/command-palette';

/**
 * The authenticated application shell.
 *
 * Sidebar + header are constant; only the `<Outlet />` swaps. The Suspense
 * boundary sits *inside* the shell so a lazily-loaded route never blanks the
 * navigation — the user keeps their bearings while a chunk downloads (§18).
 */
export function AdminLayout() {
  const { open, setOpen } = useCommandPalette();

  return (
    <SidebarProvider>
      <AdminSidebar />

      <SidebarInset className="min-w-0">
        <AdminHeader onOpenCommandPalette={() => setOpen(true)} />

        <main className="min-w-0 flex-1">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </SidebarInset>

      <CommandPalette open={open} onOpenChange={setOpen} />
    </SidebarProvider>
  );
}

/**
 * Shown while a route chunk loads. Shaped like a page header plus a table so
 * the layout does not lurch when the real content arrives (§64).
 */
function RouteFallback() {
  return (
    <div className="space-y-6 p-4 sm:p-6" role="status" aria-live="polite">
      <span className="sr-only">Loading page…</span>
      <div className="space-y-2">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="h-4 w-72" />
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
